"use client";

import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, Filter, Check } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";

interface FilterSectionProps {
    title: string;
    children: React.ReactNode;
    defaultOpen?: boolean;
}

function FilterSection({ title, children, defaultOpen = true }: FilterSectionProps) {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="border-b border-gray-100 py-4">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between w-full mb-2 group"
            >
                <span className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{title}</span>
                {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>

            <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
                <div className="pt-2 pb-2 space-y-2">
                    {children}
                </div>
            </div>
        </div>
    );
}

export function ProductFilters() {
    const router = useRouter();
    const searchParams = useSearchParams();

    const [categories, setCategories] = useState<string[]>([]);
    const [brands, setBrands] = useState<string[]>([]);
    const [priceRange, setPriceRange] = useState<string>("");
    const [inStock, setInStock] = useState<boolean>(false);

    // Sync state with URL params on mount
    useEffect(() => {
        const catParam = searchParams.get("category");
        const brandParam = searchParams.get("brand");
        const minPrice = searchParams.get("minPrice");
        const stockParam = searchParams.get("inStock");

        if (catParam) setCategories(catParam.split(","));
        if (brandParam) setBrands(brandParam.split(","));
        if (minPrice) {
            // Simplified reverse mapping for radio buttons
            if (minPrice === "0") setPriceRange("under_5000"); // Assuming max is 5000
            else if (minPrice === "5000") setPriceRange("5000_20000");
            else if (minPrice === "20000") setPriceRange("20000_50000");
            else if (minPrice === "50000") setPriceRange("over_50000");
        }
        if (stockParam === "true") setInStock(true);
    }, [searchParams]);

    // Apply Filters Logic
    const applyFilters = (newCats: string[], newBrands: string[], newPrice: string, newStock: boolean) => {
        const params = new URLSearchParams(searchParams.toString());

        // Reset page on filter change
        params.set("page", "1");

        // Categories
        if (newCats.length > 0) params.set("category", newCats.join(","));
        else params.delete("category");

        // Brands
        if (newBrands.length > 0) params.set("brand", newBrands.join(","));
        else params.delete("brand");

        // Price
        if (newPrice) {
            if (newPrice === "under_5000") {
                params.set("minPrice", "0");
                params.set("maxPrice", "5000");
            } else if (newPrice === "5000_20000") {
                params.set("minPrice", "5000");
                params.set("maxPrice", "20000");
            } else if (newPrice === "20000_50000") {
                params.set("minPrice", "20000");
                params.set("maxPrice", "50000");
            } else if (newPrice === "over_50000") {
                params.set("minPrice", "50000");
                params.delete("maxPrice");
            }
        } else {
            params.delete("minPrice");
            params.delete("maxPrice");
        }

        // Stock
        if (newStock) params.set("inStock", "true");
        else params.delete("inStock");

        router.push(`/products?${params.toString()}`, { scroll: false });
    };

    const toggleCategory = (cat: string) => {
        const newCats = categories.includes(cat)
            ? categories.filter(c => c !== cat)
            : [...categories, cat];
        setCategories(newCats);
        applyFilters(newCats, brands, priceRange, inStock);
    };

    const toggleBrand = (brand: string) => {
        const newBrands = brands.includes(brand)
            ? brands.filter(b => b !== brand)
            : [...brands, brand];
        setBrands(newBrands);
        applyFilters(categories, newBrands, priceRange, inStock);
    };

    const handlePriceChange = (range: string) => {
        const newRange = priceRange === range ? "" : range;
        setPriceRange(newRange);
        applyFilters(categories, brands, newRange, inStock);
    };

    const toggleStock = () => {
        const newStock = !inStock;
        setInStock(newStock);
        applyFilters(categories, brands, priceRange, newStock);
    };

    const resetFilters = () => {
        setCategories([]);
        setBrands([]);
        setPriceRange("");
        setInStock(false);
        router.push("/products", { scroll: false });
    };

    return (
        <div className="bg-white rounded-2xl border border-gray-100 p-6 sticky top-24">
            <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-100">
                <Filter className="w-5 h-5 text-blue-600" />
                <h3 className="font-bold text-lg text-secondary">Filters</h3>
            </div>

            {/* Categories */}
            <FilterSection title="Categories">
                {["Networking", "WiFi Access Points", "CCTV Cameras", "Switches", "PABX Systems", "Server", "Communication", "Security"].map((cat) => (
                    <label key={cat} className="flex items-center gap-3 cursor-pointer group">
                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${categories.includes(cat) ? "bg-blue-600 border-blue-600" : "border-gray-300 group-hover:border-blue-500"}`}>
                            <Check className={`w-3 h-3 text-white ${categories.includes(cat) ? "opacity-100" : "opacity-0"}`} />
                        </div>
                        <input
                            type="checkbox"
                            className="hidden"
                            checked={categories.includes(cat)}
                            onChange={() => toggleCategory(cat)}
                        />
                        <span className={`text-sm transition-colors ${categories.includes(cat) ? "text-blue-600 font-medium" : "text-gray-600 group-hover:text-blue-600"}`}>{cat}</span>
                    </label>
                ))}
            </FilterSection>

            {/* Brands */}
            <FilterSection title="Brands">
                {["Ubiquiti", "Ruijie", "Hikvision", "TP-Link", "Dell", "Cisco", "D-Link", "Yeastar", "Logitech", "APC"].map((brand) => (
                    <label key={brand} className="flex items-center gap-3 cursor-pointer group">
                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${brands.includes(brand) ? "bg-blue-600 border-blue-600" : "border-gray-300 group-hover:border-blue-500"}`}>
                            <Check className={`w-3 h-3 text-white ${brands.includes(brand) ? "opacity-100" : "opacity-0"}`} />
                        </div>
                        <input
                            type="checkbox"
                            className="hidden"
                            checked={brands.includes(brand)}
                            onChange={() => toggleBrand(brand)}
                        />
                        <span className={`text-sm transition-colors ${brands.includes(brand) ? "text-blue-600 font-medium" : "text-gray-600 group-hover:text-blue-600"}`}>{brand}</span>
                    </label>
                ))}
            </FilterSection>

            {/* Price Range */}
            <FilterSection title="Price Range">
                <div className="space-y-3">
                    {[
                        { label: "Under LKR 5,000", value: "under_5000" },
                        { label: "LKR 5,000 - 20,000", value: "5000_20000" },
                        { label: "LKR 20,000 - 50,000", value: "20000_50000" },
                        { label: "LKR 50,000+", value: "over_50000" }
                    ].map((option) => (
                        <label key={option.value} className="flex items-center gap-3 cursor-pointer group">
                            <input
                                type="radio"
                                name="price"
                                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                                checked={priceRange === option.value}
                                onChange={() => handlePriceChange(option.value)}
                                onClick={() => { if (priceRange === option.value) handlePriceChange(option.value) }} // Allow uncheck
                            />
                            <span className="text-sm text-gray-600">{option.label}</span>
                        </label>
                    ))}
                </div>
            </FilterSection>

            {/* Stock Status */}
            <FilterSection title="Availability">
                <label className="flex items-center gap-3 cursor-pointer group">
                    <div className={`w-9 h-5 rounded-full relative transition-colors ${inStock ? "bg-blue-600" : "bg-gray-200"}`}>
                        <input
                            type="checkbox"
                            className="hidden"
                            checked={inStock}
                            onChange={toggleStock}
                        />
                        <div className={`w-3 h-3 bg-white rounded-full absolute top-1 transition-transform ${inStock ? "left-5" : "left-1"}`}></div>
                    </div>
                    <span className="text-sm text-gray-600">In Stock Only</span>
                </label>
            </FilterSection>

            <button
                onClick={resetFilters}
                className="w-full mt-6 bg-gray-100 text-gray-600 font-bold py-3 rounded-xl hover:bg-gray-200 transition-colors text-sm"
            >
                Reset Filters
            </button>
        </div>
    );
}
